'use client'

import type { ReactNode } from 'react'
import { useCallback } from 'react'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'

// import { Check } from 'lucide-react'

import type { BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Button, NumberFlow, toast } from '@ui/web/components'
import { FaveIcon } from '@ui/web/icon'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../context'

interface RenderTriggerProps {
  onClick: () => void
  disabled: boolean
  isLoved: boolean
}

interface LoveBookButtonProps {
  data: BookDetails
  /**
   * Type button is for modal
   * and icon is for reader
   */
  type?: 'button' | 'icon'
  renderLinkLovedBooks: RenderLink
  className?: string
  renderTrigger?: (props: RenderTriggerProps) => ReactNode
}

export function LoveBookButton({
  data,
  type = 'button',
  // renderLinkLovedBooks,
  className,
  renderTrigger,
}: LoveBookButtonProps) {
  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const [doILoveThisBookQuery, countLoveBooksQuery] = useQueries({
    queries: [
      trpc.loveBook.doILoveThisBook.queryOptions({ bookId: data.id }),
      trpc.loveBook.countLoveBooks.queryOptions({ bookId: data.id }),
    ],
  })

  const likeBookMutation = useMutation(
    trpc.loveBook.likeBook.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.doILoveThisBook.queryKey({ bookId: data.id }) })
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.countLoveBooks.queryKey({ bookId: data.id }) })
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.getAllLovedBooks.infiniteQueryKey() })
      },
    }),
  )

  const unlikeBookMutation = useMutation(
    trpc.loveBook.unlikeBook.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.countLoveBooks.queryKey({ bookId: data.id }) })
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.doILoveThisBook.queryKey({ bookId: data.id }) })
        await queryClient.invalidateQueries({ queryKey: trpc.loveBook.getAllLovedBooks.infiniteQueryKey() })
      },
    }),
  )

  const isLoved = doILoveThisBookQuery.isSuccess && doILoveThisBookQuery.data === true

  const handleClick = useCallback(() => {
    // let toastId: string | number
    // let likeType: 'create' | 'delete'

    if (isLoved) {
      // toastId = toast.loading('Menghapus buku dari Pilihanku...')
      // likeType = 'delete'
      unlikeBookMutation.mutate(
        { bookId: data.id },
        {
          onSuccess: () => {
            // toast.dismiss(toastId)
            // toast.success('Buku berhasil dihapus dari Pilihanku')
          },
          onError: () => {
            // toast.dismiss(toastId)
            toast.error('Gagal menghapus buku dari Pilihanku')
          },
        },
      )
    } else {
      // toastId = toast.loading('Menyimpan buku ke Pilihanku...')
      // likeType = 'create'
      likeBookMutation.mutate(
        { bookId: data.id },
        {
          onSuccess: () => {
            // toast.dismiss(toastId)
            // toast.custom(() => <LoveBookToast data={data} renderLink={renderLinkLovedBooks} type={likeType} />, {
            //   duration: 10 * 1000,
            // })
          },
          onError: () => {
            // toast.dismiss(toastId)
            toast.error('Gagal menyimpan buku ke Pilihanku')
          },
        },
      )
    }
  }, [
    isLoved,
    data.id,
    likeBookMutation,
    unlikeBookMutation,
    // renderLinkLovedBooks
  ])

  const isPending = likeBookMutation.isPending || unlikeBookMutation.isPending

  if (renderTrigger) {
    return <>{renderTrigger({ onClick: handleClick, disabled: isPending, isLoved })}</>
  }

  if (type === 'icon') {
    return (
      <Button
        size='icon'
        className={cn('srd:bg-transparent srd:bg-none', isLoved && 'srd:bg-blue-100/20')}
        disabled={isPending}
        onClick={handleClick}
      >
        <FaveIcon className='srd:size-6 srd:text-neutral-black srd:shrink-0' color='var(--neutral-black)' />
      </Button>
    )
  }

  return (
    <div
      className={cn(
        'srd:w-full srd:h-fit srd:flex srd:flex-col srd:gap-y-2 srd:text-center srd:items-center srd:justify-center',
        className,
      )}
    >
      <Button variant='outline' disabled={isPending} onClick={handleClick} className='srd:w-full'>
        <>
          <FaveIcon className='srd:size-6 srd:mr-2 srd:shrink-0' color='var(--neutral-black)' />
          Simpan ke Pilihanku
        </>
      </Button>

      <p className='srd:text-xs srd:font-light'>
        Disimpan oleh{' '}
        <NumberFlow
          value={countLoveBooksQuery.data ?? 0}
          className='srd:text-complementary-yellow-100 srd:font-medium'
        />{' '}
        orang
      </p>
    </div>
  )
}

// interface LoveBookToastProps {
//   data: Pick<BookDetails, 'coverUrl' | 'title'>
//   renderLink: RenderLink
//   type: 'create' | 'delete'
// }

// function LoveBookToast({ data, renderLink, type }: LoveBookToastProps) {
//   if (type === 'delete') {
//     return (
//       <div className='srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:p-4'>
//         <p className='srd:text-main-red-100 srd:flex srd:items-start srd:text-sm'>
//           <Check className='srd:w-4 srd:h-4 srd:mr-2 srd:shrink-0' />
//           {data.title} telah dikeluarkan dari Pilihanku
//         </p>
//       </div>
//     )
//   }

//   return (
//     <div className='srd:flex srd:items-center srd:justify-between srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:gap-x-4 srd:p-4'>
//       <div className='srd:flex srd:items-center srd:gap-x-2'>
//         <img
//           src={data.coverUrl}
//           alt={data.title}
//           width={60}
//           height={60}
//           draggable={false}
//           className='srd:rounded-md srd:aspect-3/4 srd:object-cover srd:overflow-clip srd:shrink-0'
//         />

//         <p className='srd:text-complementary-purple-100 srd:font-semibold srd:text-xs'>
//           {data.title} tersimpan di Pilihanku
//         </p>
//       </div>

//       <Button asChild variant='outline' className='srd:w-fit srd:h-fit srd:px-2 srd:py-1 srd:text-xs' size='sm'>
//         {renderLink({
//           children: 'Kunjungi Pilihanku',
//           className: 'srd:w-fit srd:h-fit srd:shrink-0',
//         })}
//       </Button>
//     </div>
//   )
// }
